import React, { useEffect, useState } from 'react'

import { Box, Link, Stack, Typography } from '@mui/material'
import { SearchIcon } from 'lucide-react'

import { Alert, Button, Input, Modal } from '@repo/ui'
import { SelectField } from 'components'
import { useCanAccess } from 'hooks'
import {
  useCreateMarketSource,
  useDetectMarketSourceFeed,
  useMarketSourceCompetitorsQuery,
  useUpdateMarketSource,
} from 'hooks/api'
import { getErrorMessage } from 'utils'
import type {
  IFeedDetectResult,
  IMarketSource,
  MarketSourceCategory,
  MarketSourceKind,
} from 'services/marketSourcesApi'

const NONE = '__none__'

const KIND_OPTIONS = [
  {
    value: 'rss',
    label: 'RSS / Atom',
    description: 'A syndication feed. Polled on the cadence below; new entries arrive as Content on their own.',
  },
  { value: 'sitemap', label: 'Sitemap', description: 'An XML sitemap. Also polled — use it when a site publishes no feed.' },
  {
    value: 'social',
    label: 'Social',
    description:
      'TikTok, Instagram, LinkedIn. Never fetched. Register it so content you paste in by hand can name where it came from.',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'A newsletter, a report series, anything else. Never fetched — a label for provenance only.',
  },
]

const CATEGORY_OPTIONS = [
  { value: 'market', label: 'Market', description: 'News, analysis, a competitor’s blog. New items can become content ideas.' },
  {
    value: 'regulatory',
    label: 'Regulatory',
    description: 'A regulator or official journal. New items become Regulation ideas (explainers kept current).',
  },
]

export interface MarketSourcePrefill {
  competitorDomain?: string
  name?: string
}

interface Props {
  open: boolean
  onClose: () => void
  /** Edit this source; omitted = add a new one. */
  source?: IMarketSource | null
  /** Add with these fields filled in (the SEO tab's "Add feed" on a competitor). */
  prefill?: MarketSourcePrefill | null
  onSaved?: (row: IMarketSource) => void
}

/**
 * VRG-741 — Add / Edit source, one form for both entry points: Content → Sources ("Add source",
 * "Edit source") and GTM → SEO → Competitors ("Add feed", prefilled with the competitor).
 *
 * A competitor's feed is just a market source with `competitorDomain` set: the competitor's profile
 * stays on the SEO tab, the feed is polled by the same poller into the same corpus and review queue.
 * "Find feed" looks for a declared feed or a common feed/sitemap path on the competitor's (or the
 * URL's) domain through the API's guarded fetcher; it saves nothing. Auto-extract is never set here:
 * it is a billed model call, turned on per source from the row menu.
 */
export const MarketSourceFormModal = ({ open, onClose, source, prefill, onSaved }: Props) => {
  const editing = Boolean(source)
  const createSource = useCreateMarketSource()
  const updateSource = useUpdateMarketSource()
  const detect = useDetectMarketSourceFeed()
  const { data: competitors = [] } = useMarketSourceCompetitorsQuery(open)
  const { isHasAccess: canManage } = useCanAccess({ action: 'internal_market_intel_manage' })

  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [kind, setKind] = useState<MarketSourceKind>('rss')
  const [category, setCategory] = useState<MarketSourceCategory>('market')
  const [competitor, setCompetitor] = useState<string>(NONE)
  const [error, setError] = useState<string | null>(null)
  const [found, setFound] = useState<IFeedDetectResult | null>(null)

  useEffect(() => {
    if (!open) return
    setName(source?.name ?? prefill?.name ?? '')
    setUrl(source?.url ?? '')
    setKind((source?.kind as MarketSourceKind) ?? 'rss')
    setCategory(source?.category ?? 'market')
    setCompetitor(source?.competitorDomain ?? prefill?.competitorDomain ?? NONE)
    setError(null)
    setFound(null)
  }, [open, source, prefill])

  const pending = createSource.isPending || updateSource.isPending
  const competitorDomain = competitor === NONE ? null : competitor
  const detectDomain = competitorDomain ?? (url.trim() ? url.trim() : '')

  const runDetect = async () => {
    setError(null)
    try {
      const r = await detect.mutateAsync(detectDomain)
      setFound(r)
      const best = r.candidates[0]
      if (best) {
        setUrl(best.url)
        setKind(best.kind)
        if (!name.trim() && best.title) setName(best.title)
      }
    } catch (e) {
      setError(getErrorMessage(e) || 'Could not look for a feed on that site.')
    }
  }

  const save = async () => {
    if (!name.trim() || (!editing && !url.trim())) return
    setError(null)
    try {
      const row = editing
        ? await updateSource.mutateAsync({ id: source!.id, name: name.trim(), kind, category, competitorDomain })
        : await createSource.mutateAsync({ name: name.trim(), url: url.trim(), kind, category, competitorDomain })
      onSaved?.(row)
      onClose()
    } catch (e) {
      setError(getErrorMessage(e) || 'Could not save that source.')
    }
  }

  const competitorOptions = [
    { value: NONE, label: 'None', description: 'Not a competitor’s feed.' },
    ...competitors.map((c) => ({
      value: c.domain,
      label: c.name?.trim() || c.domain,
      description: `${c.domain} · ${c.feedCount} feed${c.feedCount === 1 ? '' : 's'}`,
    })),
  ]

  return (
    <Modal
      open={open}
      title={editing ? 'Edit source' : prefill?.competitorDomain ? 'Add competitor feed' : 'Add source'}
      onClose={onClose}
      description="Polled on a cadence, with robots.txt respected and one request per second per domain. Only RSS and sitemap feeds are polled. Polling and turning new items into content ideas make no model calls."
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={save}
            disabled={!name.trim() || (!editing && !url.trim())}
            loading={pending}
          >
            {editing ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      {error && <Alert severity="error" description={error} sx={{ mt: 1, mb: 3 }} />}
      <Stack spacing={4}>
        <SelectField
          label="Competitor (optional)"
          value={competitor}
          onChange={(v) => setCompetitor(v as string)}
          options={competitorOptions}
          search
          disabled={pending}
        />
        <Input
          label="Name"
          placeholder="Business of Fashion"
          value={name}
          onChange={(value: string) => setName(value)}
          disabled={pending}
        />
        <Stack spacing={2}>
          <Stack direction="row" spacing={3} alignItems="flex-end">
            <Box sx={{ flex: 1 }}>
              <Input
                label="Feed URL"
                placeholder="https://example.com/feed.xml"
                value={url}
                onChange={(value: string) => setUrl(value)}
                disabled={pending || editing}
              />
            </Box>
            {!editing && (
              <Button
                variant="secondary"
                startIcon={<SearchIcon strokeWidth={1} size={18} />}
                onClick={runDetect}
                loading={detect.isPending}
                disabled={!detectDomain || pending || !canManage}
                disabledReason={!canManage ? 'Finding a feed needs the market-data manage permission.' : undefined}
              >
                Find feed
              </Button>
            )}
          </Stack>
          {editing && (
            <Typography variant="body4" color="text.secondary">
              The URL is fixed once a source exists: its items are attributed to it. Add a new source for a new URL.
            </Typography>
          )}
          {found && (
            <Typography variant="body4" color="text.secondary" component="div">
              {found.candidates.length ? (
                <>
                  Found on {found.domain}:{' '}
                  {found.candidates.map((c, i) => (
                    <React.Fragment key={c.url}>
                      {i > 0 ? ', ' : ''}
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => {
                          setUrl(c.url)
                          setKind(c.kind)
                        }}
                        sx={{ font: 'inherit', fontWeight: c.url === url ? 600 : 400 }}
                      >
                        {c.kind === 'rss' ? 'RSS' : 'sitemap'} {c.url.replace(/^https?:\/\//, '')}
                      </Link>
                    </React.Fragment>
                  ))}
                  . {found.candidates[0]?.via === 'common_path' ? 'Found at a common address.' : 'Declared by the site.'}
                </>
              ) : (
                <>
                  No feed or sitemap found on {found.domain} ({found.tried.length} addresses tried). Paste the URL if you
                  know it.
                </>
              )}
            </Typography>
          )}
        </Stack>
        <SelectField
          label="Kind"
          value={kind}
          onChange={(value) => setKind(value as MarketSourceKind)}
          options={KIND_OPTIONS}
          disabled={pending}
        />
        <SelectField
          label="Category"
          value={category}
          onChange={(value) => setCategory(value as MarketSourceCategory)}
          options={CATEGORY_OPTIONS}
          disabled={pending}
        />
      </Stack>
    </Modal>
  )
}
